import React, { useState } from "react";
import { Panel } from "../components/Panel";
import { usePlayer } from "../context/PlayerContext";
import { apiErr } from "../api/client";
import { LogIn, UserPlus, Shield } from "lucide-react";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

export default function AuthModal({ onClose }) {
  const { profile, registerAccount, loginAccount } = usePlayer();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", displayName: profile?.displayName || "" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      if (mode === "register") await registerAccount({ email: form.email, password: form.password, displayName: form.displayName });
      else await loginAccount({ email: form.email, password: form.password });
      onClose();
    } catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  };

  const field = (key, type, ph, testid) => (
    <input type={type} placeholder={ph} value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      className="w-full px-4 py-3 rounded-xl bg-slate-800/80 border border-slate-700 focus:border-amber-500 outline-none text-white"
      data-testid={testid} />
  );

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Panel title={mode === "register" ? "Create Account" : "Log In"} onClose={onClose}>
      {mode === "register" && (
        <div className="card p-3 mb-4 border-amber-500/30 flex items-start gap-2 text-sm text-slate-300">
          <Shield size={18} className="text-amber-400 shrink-0 mt-0.5" />
          Your current guest progress will be safely migrated to this new account.
        </div>
      )}
      <div className="space-y-3">
        {mode === "register" && field("displayName", "text", "Display Name", "auth-name")}
        {field("email", "email", "Email", "auth-email")}
        {field("password", "password", "Password (min 6 chars)", "auth-password")}
        {err && <div className="text-red-400 text-sm" data-testid="auth-error">{err}</div>}
        <button disabled={busy} onClick={submit} className="btn-primary w-full py-3 flex items-center justify-center gap-2" data-testid="auth-submit">
          {mode === "register" ? <><UserPlus size={18} /> Create & Save</> : <><LogIn size={18} /> Log In</>}
        </button>
        <div className="flex items-center gap-2 my-1">
          <div className="flex-1 h-px bg-slate-700" /><span className="text-xs text-slate-500">OR</span><div className="flex-1 h-px bg-slate-700" />
        </div>
        <button onClick={googleLogin} className="w-full py-3 rounded-xl bg-white text-slate-800 font-bold flex items-center justify-center gap-2 hover:bg-slate-100 transition-colors" data-testid="google-login-btn">
          <GoogleIcon /> Continue with Google
        </button>
        <button onClick={() => { setMode(mode === "register" ? "login" : "register"); setErr(null); }}
          className="w-full text-center text-cyan-400 text-sm font-semibold" data-testid="auth-switch">
          {mode === "register" ? "Already have an account? Log in" : "Need an account? Register"}
        </button>
      </div>
      <div className="text-[11px] text-slate-500 text-center mt-3">Your guest progress is safely migrated when you sign in.</div>
    </Panel>
  );
}
